import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {ListenerGroup} from './shared/keyboard-viewport.util';
import {MmKeyboardStateService} from './keyboard-state.service';

// Capped autosize textareas scroll internally; WebKit chases the hidden caret by panning the
// DOCUMENT (the white-bar creep). Growing the element to its content height while the keyboard
// is open keeps the caret inside the element, so WebKit has nothing to chase.
// The '!important' outranks CdkTextareaAutosize's plain inline height.
@Injectable({providedIn: 'root'})
export class MmKeyboardTextareaGrowService {

  private listeners = new ListenerGroup();
  private stateSub: Subscription | null = null;
  private grown = new Set<HTMLTextAreaElement>();

  constructor(private keyboardState: MmKeyboardStateService) {}

  start(): void {
    if (this.listeners.active || typeof document === 'undefined') {
      return;
    }
    this.listeners.add(document, 'input', (event) => this.growTarget(event.target), {capture: true});
    this.listeners.add(document, 'focusin', (event) => this.growTarget(event.target), {capture: true});
    this.stateSub = this.keyboardState.keyboardOpen$.subscribe(open => {
      if (open) {
        this.growTarget(document.activeElement);
      } else {
        this.restoreAll();
      }
    });
  }

  stop(): void {
    this.listeners.clear();
    this.stateSub?.unsubscribe();
    this.stateSub = null;
    this.restoreAll();
  }

  private growTarget(target: EventTarget | null): void {
    if (!this.keyboardState.isKeyboardOpen) {
      return;
    }
    if (!(target instanceof HTMLTextAreaElement) || !target.classList.contains('cdk-textarea-autosize')) {
      return;
    }
    if (target.scrollHeight > target.clientHeight) {
      target.style.setProperty('height', `${target.scrollHeight}px`, 'important');
      this.grown.add(target);
    }
  }

  private restoreAll(): void {
    this.grown.forEach(textarea => textarea.style.removeProperty('height'));
    this.grown.clear();
  }
}
